import type { V1BillingTier } from "schemas/generated/billing";

import { useCurrentWorkspaceId } from "@editor/contexts/WorkspaceDashboardContext";
import { subscriptionRouter } from "@editor/trpc/subscription";

import { skipToken } from "@tanstack/react-query";
import { getV1BillingPlanInfoByTier, V1BillingTiers } from "schemas/billing";

import useCurrentProjectId from "./useCurrentProjectId";

/**
 * get the current projects active subscription details (name, paymentProcessor,
 * startDate, monthlyAmount id, etc.)
 */
export function useSubscriptionDetails() {
  const workspaceId = useCurrentWorkspaceId();
  const projectId = useCurrentProjectId();

  const workspaceActiveSubscription =
    subscriptionRouter.getActiveSubscription.useQuery(
      workspaceId
        ? {
            workspaceId,
            projectId,
          }
        : skipToken,
    );

  return workspaceActiveSubscription;
}

/**
 * gets the current projects active subscription info (CTA, description,
 * features, plan limits etc.)
 */
export function useSubscriptionInfo() {
  const { data: subscriptionDetails, ...rest } = useSubscriptionDetails();
  let activeSubscriptionTier: V1BillingTier = V1BillingTiers.FREE;

  const isSubscriptionAlreadyEnded =
    subscriptionDetails?.endedAt && subscriptionDetails.endedAt <= new Date();

  if (subscriptionDetails?.v1BillingTier && !isSubscriptionAlreadyEnded) {
    activeSubscriptionTier = subscriptionDetails.v1BillingTier;
  }

  return {
    ...rest,
    subscriptionInfo: getV1BillingPlanInfoByTier(activeSubscriptionTier),
  };
}

export function useSubscriptionTier(): V1BillingTier {
  const { subscriptionInfo } = useSubscriptionInfo();
  return subscriptionInfo.tier ?? "free";
}
